import { useEffect, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import Modal from './Modal';
import { incentiveAPI } from '../services/api';
import { FormErrorBanner } from './FormErrorBanner';
import { BlockLoader } from './loading';

/**
 * One promoter's individual sales for a month — each with its own dates and
 * details, and whether it counted towards incentive.
 *
 * ── WHY THIS EXISTS ──────────────────────────────────────────────────────────
 * Every other screen shows a promoter's sales as one number. That number cannot
 * answer "was my incentive worked out per the rule", because the rule judges
 * individual sales: a date gap is a fact about ONE sale. This is the only place
 * the Unbrick Date, the Invoice Date and the verdict sit on the same row.
 *
 * ⭐ The verdicts are the server's, from the same `judgeCase` the payout runs. The
 * modal never decides for itself whether a sale counts — if it did, the screen
 * built to check the payout could disagree with the payout.
 *
 * Opened from three places: the Sales tab, the Results drill-down and the
 * Incentive payout — the same component, so all three agree.
 */

interface CaseRow {
  id: string;
  reference: string | null;
  saleDate: string | null;
  storeCode: string | null;
  productLabel: string;
  customValues: Record<string, unknown>;
  counted: boolean | null;
  reason: string | null;
  gaps: { ruleId: string; days: number | null }[];
}

interface CasesPayload {
  employeeCode: string;
  employeeName: string | null;
  planName: string | null;
  fields: { id: string; label: string; fieldType: string }[];
  rules: { id: string; description: string }[];
  /** A month split between plans: which other plan paid some days, and whether it judges sales differently. */
  note?: string | null;
  sales: CaseRow[];
  summary: { total: number; counted: number; excluded: number };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  month: string;
  employeeCode: string | null;
  /** Narrows to one product when opened from a product-scoped view. */
  productId?: string | null;
  skuId?: string | null;
  productLabel?: string | null;
  /**
   * A month split between plans: the plan whose verdicts to show, so the list
   * matches the working it was opened from. Omitted = the plan the row is filed under.
   */
  planId?: string | null;
}

type Show = 'all' | 'counted' | 'excluded';

export default function SalesCasesModal({
  isOpen, onClose, projectId, month, employeeCode, productId, skuId, productLabel, planId,
}: Props) {
  const [data, setData] = useState<CasesPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [show, setShow] = useState<Show>('all');

  useEffect(() => {
    if (!isOpen || !employeeCode) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setShow('all');
    incentiveAPI.cases(projectId, {
      month, employeeCode, productId: productId ?? undefined, skuId: skuId ?? undefined,
      planId: planId ?? undefined,
    })
      .then((res) => { if (!cancelled) setData(res.data.data); })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.message || 'Could not load the sales.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, projectId, month, employeeCode, productId, skuId, planId]);

  const rows = useMemo(() => {
    const all = data?.sales ?? [];
    if (show === 'counted') return all.filter((s) => s.counted !== false);
    if (show === 'excluded') return all.filter((s) => s.counted === false);
    return all;
  }, [data, show]);

  const hasRules = (data?.rules.length ?? 0) > 0;
  const fmt = (v: unknown): string => {
    if (v === null || v === undefined || v === '') return '—';
    return Array.isArray(v) ? v.join(', ') : String(v);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={data
        ? `${data.employeeName ?? data.employeeCode} · ${productLabel ?? 'all products'} · ${month}`
        : 'Sales'}
      size="xl"
    >
      {loading ? (
        <BlockLoader className="py-16" />
      ) : error ? (
        <FormErrorBanner message={error} />
      ) : data ? (
        <div className="space-y-4">
          {/* What the rule is, stated once, so each row's verdict is readable. */}
          <div className="flex items-start gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-700">
            <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <div>
              {data.planName
                ? <>Paid under <strong>{data.planName}</strong>.</>
                : <>No incentive plan covers this promoter, so no sale is judged.</>}
              {hasRules ? (
                <ul className="mt-1 list-disc pl-4">
                  {data.rules.map((r) => <li key={r.id}>{r.description}</li>)}
                </ul>
              ) : data.planName ? (
                <span> Its plan has no rule about individual sales, so every sale counts.</span>
              ) : null}
              {data.note && <p className="mt-1">{data.note}</p>}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-700">
              <strong>{data.summary.total.toLocaleString()}</strong> sale
              {data.summary.total === 1 ? '' : 's'}
              {hasRules && (
                <>
                  {' '}— <strong className="text-green-700">{data.summary.counted.toLocaleString()}</strong> counted,{' '}
                  <strong className={data.summary.excluded > 0 ? 'text-amber-700' : ''}>
                    {data.summary.excluded.toLocaleString()}
                  </strong> excluded by the rule
                </>
              )}
            </p>
            {hasRules && data.summary.excluded > 0 && (
              <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
                {([['all', 'All'], ['counted', 'Counted'], ['excluded', 'Excluded']] as const).map(([k, l]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setShow(k)}
                    className={`rounded-md px-3 py-1 text-xs font-medium ${
                      show === k ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600'
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            )}
          </div>

          {data.sales.length === 0 ? (
            <p className="rounded-lg border border-gray-200 px-3 py-10 text-center text-sm text-gray-500">
              No individual sales recorded for this promoter{productLabel ? ` on ${productLabel}` : ''} in {month}.
            </p>
          ) : (
            <div className="max-h-[28rem] overflow-auto rounded-lg border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <th className="whitespace-nowrap px-3 py-2">Reference</th>
                    <th className="whitespace-nowrap px-3 py-2">Sale date</th>
                    <th className="whitespace-nowrap px-3 py-2">Store</th>
                    {!productLabel && <th className="whitespace-nowrap px-3 py-2">Product</th>}
                    {/* One column per sales detail this project records — the
                        Unbrick and Invoice dates the rule reads live here. */}
                    {data.fields.map((f) => (
                      <th key={f.id} className="whitespace-nowrap px-3 py-2">{f.label}</th>
                    ))}
                    {hasRules && <th className="whitespace-nowrap px-3 py-2 text-right">Gap</th>}
                    {hasRules && <th className="whitespace-nowrap px-3 py-2">Counts?</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r) => (
                    <tr key={r.id} className={r.counted === false ? 'bg-amber-50/50' : ''}>
                      <td className="whitespace-nowrap px-3 py-1.5 font-medium text-gray-900">{r.reference ?? '—'}</td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-700">{r.saleDate ?? '—'}</td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-600">{r.storeCode ?? '—'}</td>
                      {!productLabel && <td className="whitespace-nowrap px-3 py-1.5 text-gray-600">{r.productLabel}</td>}
                      {data.fields.map((f) => (
                        <td key={f.id} className="whitespace-nowrap px-3 py-1.5 text-gray-700">
                          {fmt(r.customValues[f.id])}
                        </td>
                      ))}
                      {hasRules && (
                        <td className="whitespace-nowrap px-3 py-1.5 text-right text-gray-600">
                          {r.gaps.length === 0 ? '—'
                            : r.gaps.map((g) => (g.days === null ? '?' : `${g.days}d`)).join(' / ')}
                        </td>
                      )}
                      {hasRules && (
                        <td className="px-3 py-1.5">
                          {r.counted === null ? (
                            <span className="text-xs text-gray-400">no rule</span>
                          ) : r.counted ? (
                            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                              Counted
                            </span>
                          ) : (
                            <span className="text-xs text-amber-800" title={r.reason ?? ''}>
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium">Excluded</span>
                              {r.reason && <span className="ml-2">{r.reason}</span>}
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
